// Dialogs of the Project Overview: create / edit an overview, export the PDF,
// and read the deficiency criteria.
import React, { useEffect, useState } from 'react';
import { ANALYSES, ANALYSIS_NAMES, POINT_PAD_MI } from './overviewModel';
import { SEVERITY_DEFAULTS, SEVERITY_FIXED, SEVERITY_SPECS, isDefaultSettings } from './severitySettings';
import { AnalysisTag } from './OverviewTables';
import TopNSelect from '../Safety/TopNSelect';
import { createOverview, deleteOverview, fetchCandidateProjects, fetchPdf, updateOverview } from './overviewApi';

export const REPORT_NAME = 'Corridor Safety Summary';

export function Modal({ title, onClose, children, labelledBy = 'ov-dialog-title', tall = false }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="safety-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`safety-modal ${tall ? 'ov-modal-tall' : ''}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        <div className="safety-modal-head">
          <h2 id={labelledBy}>{title}</h2>
          <button type="button" className="lg-close" onClick={onClose} title="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Always-open project checklist: filter box, select all / clear, a list that fills the dialog. */
function ProjectChecklist({ options, selected, onChange, loading }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  const chosen = new Set(selected);
  const toggle = (key) => onChange(chosen.has(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  return (
    <div className="ov-checklist">
      <div className="ov-checklist-tools">
        <input id="ov-project-filter" type="search" className="ov-input" placeholder="Filter projects…" value={query}
          onChange={(e) => setQuery(e.target.value)} aria-label="Filter projects" />
        <button type="button" className="safety-btn safety-btn-ghost"
          onClick={() => onChange(Array.from(new Set([...selected, ...shown.map((o) => o.key)])))}>
          {q ? 'Select All Shown' : 'Select All'}
        </button>
        <button type="button" className="safety-btn safety-btn-ghost"
          onClick={() => onChange(q ? selected.filter((k) => !shown.some((o) => o.key === k)) : [])}>
          {q ? 'Clear All Shown' : 'Clear All'}
        </button>
      </div>
      <div className="ov-checklist-list" role="listbox" aria-multiselectable="true" aria-label="Projects">
        {loading && <div className="safety-muted">Loading projects…</div>}
        {!loading && !shown.length && <div className="safety-muted">No project matches.</div>}
        {shown.map((o) => (
          <label key={o.key} className={`safety-picker-item ${chosen.has(o.key) ? 'on' : ''}`} htmlFor={`ov-p-${o.key}`}>
            <input id={`ov-p-${o.key}`} type="checkbox" checked={chosen.has(o.key)} onChange={() => toggle(o.key)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/** Create a new overview (overview = null) or edit / delete an existing one. */
export function ManagerDialog({ overview, onClose, onSaved, onDeleted }) {
  const [name, setName] = useState(overview?.name || '');
  const [description, setDescription] = useState(overview?.description || '');
  const [selected, setSelected] = useState((overview?.projects || []).map((p) => String(p.id)));
  const [projects, setProjects] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    fetchCandidateProjects()
      .then((d) => { if (live) setProjects(d.projects || []); })
      .catch((e) => { if (live) { setProjects([]); setError(e.message); } });
    return () => { live = false; };
  }, []);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = { name: name.trim(), description: description.trim(), project_ids: selected.map(Number) };
      const data = overview ? await updateOverview(overview.id, payload) : await createOverview(payload);
      onSaved(data.overview);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the overview "${overview.name}"? The projects and their reports are not touched.`)) return;
    setBusy(true);
    try {
      await deleteOverview(overview.id);
      onDeleted(overview.id);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const options = (projects || []).map((p) => ({ key: String(p.id), label: `#${p.id} · ${p.name}` }));
  return (
    <Modal title={overview ? 'Edit overview' : 'New overview'} onClose={onClose} tall>
      <p className="safety-muted safety-modal-lede ov-lede-compact">
        A named set of RSA projects shown as one dashboard, for example every project of one customer order.
        People who open it see only the projects they are assigned to.
      </p>
      <div className="ov-form">
        <label className="safety-filter" htmlFor="ov-name"><span>Name</span>
          <input id="ov-name" className="ov-input" value={name} maxLength={160} onChange={(e) => setName(e.target.value)}
            placeholder="MassDOT · District 1 order" />
        </label>
        <label className="safety-filter" htmlFor="ov-desc"><span>Description (optional, printed on the PDF cover)</span>
          <input id="ov-desc" className="ov-input" value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <div className="safety-filter ov-checklist-field"><span>Projects · {selected.length} of {options.length} selected</span>
          <ProjectChecklist options={options} selected={selected} onChange={setSelected} loading={projects === null} />
        </div>
      </div>
      {error && <div className="safety-error">{error}</div>}
      <div className="safety-modal-foot">
        <span className="safety-muted">{selected.length} project{selected.length === 1 ? '' : 's'} selected</span>
        <div className="row-actions">
          {overview && <button type="button" className="safety-btn safety-btn-ghost ov-danger" disabled={busy} onClick={remove}>Delete</button>}
          <button type="button" className="safety-btn safety-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="safety-btn" disabled={busy || !name.trim() || !selected.length} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const SECTIONS = [
  ['cover', 'Cover: map, figures and key findings'], ['contents', 'Contents'], ['map', 'Map and statistics'],
  // the Safety Overview's analytic panels: offered when the page passes them (`panels`)
  ['clusterInsights', 'Cluster insights', 'panel'], ['insights', 'Insights (correlated crashes)', 'panel'],
  ['summary', 'Deficiency summary by road and by segment'], ['clusters', 'Multi-deficiency clusters'], ['sites', 'Deficiency sites'],
  ['program', 'Countermeasure program', 'panel'], ['verdicts', 'Statistical support', 'panel'],
  ['strips', 'Milepost strips, per road'], ['chapters', 'Road chapters: one page per segment'],
  ['appendix', 'Appendix: criteria, settings and methods'],
];
// the appendix parts; the ones naming an `appendixData` key exist only when the page passes that data (Safety Overview)
const APPENDIX_PARTS = [
  ['criteria', 'Criteria and severity'], ['rules', 'Deficiency rules (Filters)'],
  ['correlation', 'Crash correlation rules', 'correlation'], ['offenders', 'Worst-offender weights', 'offenders'],
  ['cmf', 'How the countermeasure (CMF) figures are calculated', 'cmf'], ['projects', 'Projects'],
];
export const BASEMAP_CHOICES = [
  ['street', 'Street (OpenStreetMap)'], ['satellite', 'Satellite (Esri)'], ['terrain', 'Terrain (OpenTopoMap)'], ['plain', 'Plain schematic'],
];

/** The Criteria dialog's rows as the PDF appendix prints them (the page's own cuts travel in options.severity). */
const criteriaRows = () => ANALYSES.map((a) => (SEVERITY_FIXED[a]
  ? { analysis: a, criterion: CRITERIA[a], fixed: SEVERITY_FIXED[a] }
  : { analysis: a, criterion: CRITERIA[a], measure: SEVERITY_SPECS[a].label, unit: SEVERITY_SPECS[a].unit, sense: SEVERITY_SPECS[a].sense }));

/** `request` = {filters, options} as on screen; this dialog adds sections, row counts, paper, theme, map and appendix.
 *  `basemap` = the base map on screen; `appendixData` = {correlation, offenders, cmf} of the Safety Overview (optional). */
export function PdfDialog({ overview, request, counts, onClose, basemap = 'street', appendixData = null, panels = null }) {
  // panels (Safety Overview): {available: {key: bool}, build(limit) -> the panels' content}
  const [sections, setSections] = useState(Object.fromEntries(SECTIONS.map(([k]) => [k, true])));
  const [parts, setParts] = useState(Object.fromEntries(APPENDIX_PARTS.map(([k]) => [k, true])));
  const [rows, setRows] = useState('screen');
  const [paper, setPaper] = useState('a4');
  const [theme, setTheme] = useState('bright');
  const [mapBg, setMapBg] = useState(basemap);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [seconds, setSeconds] = useState(0);
  const partsShown = APPENDIX_PARTS.filter(([, , extra]) => !extra || (appendixData && appendixData[extra]));
  const sectionsShown = SECTIONS.filter(([k, , kind]) => kind !== 'panel' || (panels && panels.available[k]));

  const generate = async () => {
    setBusy(true);
    setError('');
    setDone('');
    setSeconds(0);
    try {
      const limit = (onScreen) => (rows === 'screen' ? onScreen : Number(rows) || 0);
      const wanted = sectionsShown.filter(([k, , kind]) => kind === 'panel' && sections[k]);
      const panelData = wanted.length && panels ? panels.build(limit(25)) : null;
      const { blob, filename } = await fetchPdf(overview.id, {
        filters: request.filters,
        options: {
          ...request.options, paper, theme, basemap: mapBg, reportName: REPORT_NAME,
          sections: Object.fromEntries(SECTIONS.map(([k]) => [k, Boolean(sections[k] && sectionsShown.some(([s]) => s === k))])),
          panels: panelData,
          rowsSites: limit(request.options.rowsSites), rowsClusters: limit(request.options.rowsClusters),
          appendix: Object.fromEntries(APPENDIX_PARTS.map(([k]) => [k, Boolean(parts[k] && partsShown.some(([s]) => s === k))])),
          appendixData: { ...(appendixData || {}), criteria: criteriaRows() },
        },
      }, { onProgress: setSeconds });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      setDone(`Downloaded ${filename}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Export the ${REPORT_NAME}`} onClose={onClose} tall>
      <p className="safety-muted safety-modal-lede">
        One PDF for all projects of <b>{overview.name}</b>, built from the dashboard as filtered now
        ({counts.sites} sites, {counts.clusters} clusters, {counts.segments} segments). Every Report button on screen becomes a
        link in the PDF. The links open the online report after sign-in, for people assigned to the project.
      </p>
      <div className="ov-form ov-form-2">
        <fieldset className="ov-fieldset">
          <legend>Sections</legend>
          {sectionsShown.map(([k, label]) => (
            <label key={k} className="ov-check" htmlFor={`ov-sec-${k}`}>
              <input id={`ov-sec-${k}`} type="checkbox" checked={sections[k]}
                onChange={() => setSections((s) => ({ ...s, [k]: !s[k] }))} />{label}
            </label>
          ))}
          {sections.appendix && (
            <div className="ov-subchecks" role="group" aria-label="Appendix parts">
              {partsShown.map(([k, label]) => (
                <label key={k} className="ov-check" htmlFor={`ov-app-${k}`}>
                  <input id={`ov-app-${k}`} type="checkbox" checked={parts[k]}
                    onChange={() => setParts((s) => ({ ...s, [k]: !s[k] }))} />{label}
                </label>
              ))}
            </div>
          )}
        </fieldset>
        <div className="ov-form">
          <label className="safety-filter" htmlFor="ov-pdf-rows"><span>Rows per table</span>
            <TopNSelect id="ov-pdf-rows" value={rows} onChange={setRows} extra={[['screen', 'As on screen']]} className=""
              max={Math.max(counts.sites || 0, counts.clusters || 0) || null} />
          </label>
          <label className="safety-filter" htmlFor="ov-pdf-paper"><span>Paper</span>
            <select id="ov-pdf-paper" value={paper} onChange={(e) => setPaper(e.target.value)}>
              <option value="a4">A4</option><option value="letter">Letter</option>
            </select>
          </label>
          <label className="safety-filter" htmlFor="ov-pdf-theme"><span>Theme</span>
            <select id="ov-pdf-theme" value={theme} onChange={(e) => setTheme(e.target.value)}>
              <option value="bright">Bright</option><option value="dark">Dark</option>
            </select>
          </label>
          <label className="safety-filter" htmlFor="ov-pdf-map"><span>Map background</span>
            <select id="ov-pdf-map" value={mapBg} onChange={(e) => setMapBg(e.target.value)}>
              {BASEMAP_CHOICES.map(([k, label]) => <option key={k} value={k}>{label}{k === basemap ? ' · as on screen' : ''}</option>)}
            </select>
          </label>
          <p className="safety-muted">Tables keep the sort order you set on screen.</p>
        </div>
      </div>
      {error && <div className="safety-error">{error}</div>}
      {done && <div className="safety-note">{done}</div>}
      <div className="safety-modal-foot">
        <span className="safety-muted">The PDF is built on the server and downloads when ready. A large order can take a minute or two.</span>
        <div className="row-actions">
          <button type="button" className="safety-btn safety-btn-ghost" onClick={onClose}>Close</button>
          <button type="button" className="safety-btn ov-primary" disabled={busy} onClick={generate}>
            {busy ? `Building the PDF… ${seconds}s` : 'Generate and download'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const CRITERIA = {
  SSD: 'Available stopping sight distance supports less than the posted speed. Minor to 5 mph short, moderate to 10 mph, severe beyond.',
  PSD: 'Unsafe pass: the marking allows passing where the passing sight distance is not available (the reports call it Unsafe pass). Every one is severe.',
  HCA: 'Horizontal curve whose safe speed is under the posted speed (safety score below 100%). Severe under 80%, moderate under 90%.',
  GLR: 'Stations reaching the glare report threshold of glare minutes per year: the same sections the glare report lists.',
  FSA: 'Flat spot: gradient (the resultant of the cross and long slope) at or below the drainage threshold, where water can stand on the pavement.',
  GRW: 'Guardrail warranted by the embankment (slope and height); the analysis does not check whether a guardrail is already in place. Severe from 10 ft of embankment height, moderate from 5 ft.',
  ISD: 'Intersection whose available sight distance is short of the AASHTO required distance (quality below 100%). Severe under 50%, moderate under 80%.',
};

/**
 * What counts as a deficiency (fixed, from the analyses) and how it is graded (yours):
 * the minor / moderate / severe cuts per analysis and the three colours. Saved in this
 * browser and applied to the map, strips, tables, statistics and the PDF.
 */
export function CriteriaDialog({ settings, onChange, onClose }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(settings || SEVERITY_DEFAULTS)));
  const setCut = (a, k, v) => setDraft((d) => ({ ...d, thresholds: { ...d.thresholds, [a]: { ...d.thresholds[a], [k]: v === '' ? '' : Number(v) } } }));
  const setColor = (k, v) => setDraft((d) => ({ ...d, colors: { ...d.colors, [k]: v } }));
  const valid = Object.values(draft.thresholds).every((t) => Number.isFinite(t.severe) && Number.isFinite(t.moderate));
  const save = () => { onChange(draft); onClose(); };
  return (
    <Modal title="Criteria and severity" onClose={onClose} tall>
      <p className="safety-muted safety-modal-lede ov-lede-compact">
        What counts as a deficiency comes from the analyses. How it is graded is yours: set the cuts and colours below.
        They are kept for your next visits and applied everywhere, including the PDF.
      </p>
      <div className="ov-colors">
        <span className="safety-muted">Colours</span>
        {['severe', 'moderate', 'minor'].map((k) => (
          <label key={k} htmlFor={`ov-col-${k}`}>
            <input id={`ov-col-${k}`} type="color" value={draft.colors[k]} onChange={(e) => setColor(k, e.target.value)} />
            <span className={`safety-sev safety-sev-${k}`} style={{ background: draft.colors[k], color: k === 'minor' ? '#1a1200' : '#fff' }}>{k}</span>
          </label>
        ))}
      </div>
      <div className="safety-table-scroll">
        <table className="ov-crit">
          <thead><tr><th>Analysis</th><th>Deficiency and measure</th><th>Severe</th><th>Moderate</th><th>Unit</th></tr></thead>
          <tbody>
            {ANALYSES.map((a) => {
              const spec = SEVERITY_SPECS[a];
              const t = draft.thresholds[a] || {};
              if (SEVERITY_FIXED[a]) {
                return (
                  <tr key={a}>
                    <td><AnalysisTag analysis={a} /><div className="ov-crit-hint">{ANALYSIS_NAMES[a]}</div></td>
                    <td><div>{CRITERIA[a]}</div></td>
                    <td colSpan={3} className="ov-crit-hint">Always {SEVERITY_FIXED[a]}</td>
                  </tr>
                );
              }
              return (
                <tr key={a}>
                  <td><AnalysisTag analysis={a} /><div className="ov-crit-hint">{ANALYSIS_NAMES[a]}</div></td>
                  <td><div>{CRITERIA[a]}</div><div className="ov-crit-hint">{spec.label}: {spec.hint}. Minor otherwise.</div></td>
                  <td><input type="number" step="any" aria-label={`${a} severe cut`} value={t.severe} onChange={(e) => setCut(a, 'severe', e.target.value)} /></td>
                  <td><input type="number" step="any" aria-label={`${a} moderate cut`} value={t.moderate} onChange={(e) => setCut(a, 'moderate', e.target.value)} /></td>
                  <td className="ov-crit-hint">{spec.unit} · {spec.sense === 'low' ? 'lower is worse' : 'higher is worse'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="safety-modal-foot">
        <span className="safety-muted">
          Multi-deficiency cluster: the stretch where sites of at least two different analyses truly overlap, in either
          direction. An intersection has no length, so it counts {POINT_PAD_MI} mi to each side. Mileposts come from the surveyed centreline on the state LRS.
        </span>
        <div className="row-actions">
          <button type="button" className="safety-btn safety-btn-ghost" disabled={isDefaultSettings(draft)}
            onClick={() => setDraft(JSON.parse(JSON.stringify(SEVERITY_DEFAULTS)))}>Reset to defaults</button>
          <button type="button" className="safety-btn safety-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="safety-btn" disabled={!valid} onClick={save}>Save</button>
        </div>
      </div>
    </Modal>
  );
}
